import React from 'react';
import Colors from '../../config/colors';
import { FONT_USAGE } from '../../config/fonts';

// ============================================================================
// DESIGN SYSTEM CONSTANTS - Following DRY principle
// ============================================================================

export const DESIGN_TOKENS = {
    // Spacing
    spacing: {
        xs: 1,
        sm: 2,
        md: 3,
        lg: 4,
        xl: 6,
        xxl: 8,
        xxxl: 12,
    },

    // Typography
    typography: {
        fontFamily: {
            primary: FONT_USAGE.headings,
            secondary: FONT_USAGE.subheadings,
            body: FONT_USAGE.body,
        },
        fontSize: {
            xs: '0.75rem',
            sm: '0.875rem',
            md: '1rem',
            lg: '1.125rem',
            xl: '1.25rem',
            xxl: '1.5rem',
            xxxl: '2rem',
            hero: '2.5rem',
        },
        fontWeight: {
            light: 300,
            normal: 400,
            medium: 500,
            bold: 700,
            black: 900,
        },
    },

    // Colors
    colors: {
        primary: Colors.purple,
        secondary: Colors.purpleLighter,
        text: {
            primary: Colors.black,
            secondary: '#666',
            light: '#999',
        },
        background: {
            primary: Colors.white,
            secondary: Colors.purpleLighter,
            gradient: `linear-gradient(135deg, ${Colors.purple} 0%, ${Colors.purpleLighter} 100%)`,
        },
    },

    // Breakpoints
    breakpoints: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
    },

    // Shadows
    shadows: {
        light: '0 2px 8px rgba(0,0,0,0.1)',
        medium: '0 4px 16px rgba(0,0,0,0.15)',
        heavy: '0 8px 32px rgba(0,0,0,0.2)',
    },

    // Transitions
    transitions: {
        fast: '0.15s ease-in-out',
        normal: '0.3s ease-in-out',
        slow: '0.5s ease-in-out',
    },

    // Border radius
    borderRadius: {
        small: '8px',
        medium: '16px',
        large: '24px',
        pill: '9999px',
    },
};

// ============================================================================
// UTILITY FUNCTIONS - Following Single Responsibility Principle
// ============================================================================

export const createResponsiveSpacing = (xs: number, md: number, lg?: number) => ({
    xs: xs,
    md: md,
    ...(lg && { lg: lg }),
});

export const createResponsiveFontSize = (xs: string, md: string, lg?: string) => ({
    xs: xs,
    md: md,
    ...(lg && { lg: lg }),
});

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface DesignSystemProps {
    children: React.ReactNode;
}

export interface ResponsiveValue<T> {
    xs: T;
    md: T;
    lg?: T;
}

// ============================================================================
// DESIGN SYSTEM PROVIDER COMPONENT
// ============================================================================

export const DesignSystemProvider: React.FC<DesignSystemProps> = ({ children }) => {
    return <>{children}</>;
};

export default DesignSystemProvider;
